import { useState, useEffect } from "react";
import { Button, Label, Select, Tooltip } from "flowbite-react";
import { HiCheck, HiQuestionMarkCircle } from "react-icons/hi";
import { useAuth, useToast } from "../../../../hooks";
import { NOTIFICATION_TYPES } from "../../../../utils";

const EVENT_TYPES = [
  { value: "page_view", label: "Page Views" },
  { value: "session", label: "Sessions" },
  { value: "engagement", label: "Engagement Events" },
  { value: "feature_usage", label: "Feature Usage" },
  { value: "custom", label: "Custom Events" },
];

const REGIONS = [
  { value: "US", label: "United States (US)" },
  { value: "EU", label: "European Union (EU)" },
];

export const MixpanelForm = ({ integration, config, onConfigUpdate }) => {
  const { apiClient } = useAuth();
  const showToast = useToast();

  const [formData, setFormData] = useState({
    region: "US",
    event_types: [],
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Load config into form (preserve credentials from config)
  useEffect(() => {
    if (config) {
      setFormData((prev) => ({
        ...prev,
        region: config.region || "US",
        event_types: config.event_types || [],
      }));
    }
  }, [config]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setSaveSuccess(false);
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleEventTypeToggle = (eventType) => {
    setFormData((prev) => ({
      ...prev,
      event_types: prev.event_types.includes(eventType)
        ? prev.event_types.filter((t) => t !== eventType)
        : [...prev.event_types, eventType],
    }));
    setSaveSuccess(false);
  };

  const validateForm = () => {
    const newErrors = {};

    if (formData.event_types.length === 0) {
      newErrors.event_types = "Select at least one event type";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setSaveSuccess(false);

    try {
      // Merge with existing config to preserve credentials
      const updatedConfig = {
        ...config,
        ...formData,
      };

      const response = await apiClient.patch(
        `integration/team-integrations/${integration.id}/`,
        {
          config: updatedConfig,
        },
      );

      onConfigUpdate(response.data.config || updatedConfig);
      setSaveSuccess(true);
      showToast({
        content: "Mixpanel configuration saved successfully!",
        type: NOTIFICATION_TYPES.success,
      });

      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error("Failed to save Mixpanel config:", err);
      const errorMsg =
        err.response?.data?.error ||
        "Failed to save configuration. Please try again.";
      showToast({
        content: errorMsg,
        type: NOTIFICATION_TYPES.error,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-6 pb-4">
      <h3 className="mb-4 text-lg font-semibold text-gray-900">
        Configuration
      </h3>

      <div className="space-y-4">
        {/* Region */}
        <div>
          <div className="flex items-center gap-2">
            <Label htmlFor="region">Data Region</Label>
            <Tooltip content="Select the region where your Mixpanel data is stored">
              <HiQuestionMarkCircle className="h-4 w-4 text-gray-400" />
            </Tooltip>
          </div>
          <Select
            id="region"
            value={formData.region}
            onChange={(e) => handleChange("region", e.target.value)}
            className="mt-1"
          >
            {REGIONS.map((region) => (
              <option key={region.value} value={region.value}>
                {region.label}
              </option>
            ))}
          </Select>
        </div>

        {/* Event Types */}
        <div>
          <div className="flex items-center gap-2">
            <Label>Event Types to Sync</Label>
            <Tooltip content="Select which event types to import from Mixpanel">
              <HiQuestionMarkCircle className="h-4 w-4 text-gray-400" />
            </Tooltip>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {EVENT_TYPES.map((eventType) => (
              <button
                key={eventType.value}
                type="button"
                onClick={() => handleEventTypeToggle(eventType.value)}
                className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                  formData.event_types.includes(eventType.value)
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {eventType.label}
              </button>
            ))}
          </div>
          {errors.event_types && (
            <p className="mt-1 text-sm text-red-600">{errors.event_types}</p>
          )}
        </div>

        {/* Success Messages */}
        {saveSuccess && (
          <div className="flex items-center gap-2 rounded-lg bg-green-50 p-3 text-sm text-green-800">
            <HiCheck className="h-5 w-5" />
            Configuration saved successfully
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <Button
            color="blue"
            onClick={handleSave}
            disabled={saving}
            isProcessing={saving}
          >
            Save Configuration
          </Button>
        </div>
      </div>
    </div>
  );
};
